import { useMemo, useState } from 'react'

import { Filters } from './FilterControls'

import { CalendarAdminEvent, CalendarReporterEvent } from '@/apiClient'
import { toCalendarEvent } from '@/components/Calendar/Calendar'
import { useCalendarEvents } from '@/queries/calendar'
import * as urls from '@/queries/urls'
import { CalendarParams } from '@/queries/useCalendarParams'
import { eventLevel, isReporterAndEventStateEqual } from '@/utils/parrot'
import { normalize } from '@/utils/string'

export type UseFilteredCalendarEventsProps = {
  calendarParams: CalendarParams
  initialFilters: Filters
}

type CalendarListEvent = CalendarAdminEvent | CalendarReporterEvent

export const useFilteredCalendarEvents = ({
  calendarParams,
  initialFilters,
}: UseFilteredCalendarEventsProps) => {
  const [filters, setFilters] = useState<Filters>(initialFilters)

  const {
    events: allEvents,
    isFetching,
    refetch,
  } = useCalendarEvents<CalendarListEvent>({
    url: filters.reporter
      ? urls.calendarReporterEvents(filters.reporter.id)
      : urls.calendarAdminEvents(),
    calendarParams,
  })

  const filterByStates = (event: CalendarListEvent) => {
    if (!filters.state_in || filters.state_in.length === 0) {
      return true
    }

    return (
      typeof event.case.state === 'string' &&
      filters.state_in.includes(event.case.state)
    )
  }

  const filterByStatus = (event: CalendarListEvent) => {
    if (filters.reporter && 'reporter_status' in event) {
      return filters.reporter_status_in.includes(event.reporter_status)
    }

    return filters.general_status_in.includes(event.general_status)
  }

  const filterByEvent = (event: CalendarListEvent) => {
    const normalizedSearch = normalize(filters.event_search ?? '')

    return normalize(event.name).includes(normalizedSearch)
  }

  const filterByTeam = (event: CalendarListEvent) => {
    const normalizedSearch = normalize(filters.team_search ?? '')

    return normalize(event.team.name).includes(normalizedSearch)
  }

  const filterByOrganization = (event: CalendarListEvent) => {
    const normalizedSearch = normalize(filters.organization_search ?? '')

    return normalize(event.team.organization.name).includes(normalizedSearch)
  }

  const filterByEventLevel = (event: CalendarListEvent) => {
    if (!filters.event_level_in || filters.event_level_in.length === 0) {
      return true
    }

    const level = eventLevel(event)

    return level !== null && filters.event_level_in.includes(level)
  }

  const filterBookedByReporterFromDifferentState = (
    event: CalendarListEvent,
  ) => {
    if (filters.booked_by_reporter_from_different_state === undefined) {
      return true
    }

    const isEventBookedByReporterFromDifferentState =
      // admin events contain `reporter` field which we can use to check the OOS
      ('reporter' in event &&
        event.reporter !== null &&
        event.reporter !== undefined &&
        !isReporterAndEventStateEqual(event, event.reporter)) ||
      // if the events are filtered by reporter, then we are dealing with
      // "reporter events" which don't have the `reporter` field, but we can use
      // the `reporter` value from the filter to check the OOS
      (filters.reporter !== undefined &&
        !isReporterAndEventStateEqual(event, filters.reporter))

    return (
      filters.booked_by_reporter_from_different_state ===
      isEventBookedByReporterFromDifferentState
    )
  }

  const events = useMemo(
    () =>
      allEvents
        .filter(filterByStatus)
        .filter(filterByEvent)
        .filter(filterByTeam)
        .filter(filterByOrganization)
        .filter(filterByEventLevel)
        .filter(filterByStates)
        .filter(filterBookedByReporterFromDifferentState)
        .map(toCalendarEvent),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allEvents, filters],
  )

  return {
    events,

    filters,
    setFilters,

    isFetching,
    refetch,
  }
}
